import {
  Component,
  Inject,
  OnInit,
  Optional,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import {
  LoadUserPatrolData,
  StartPatrolling,
  EndPatrolling,
} from 'src/app/state-management/actions/guard-patrol.actions';
import {
  selectPatrolLoading,
  selectAssignedGeofences,
  selectPatrolStatistics,
  selectPatrolViolations,
  selectHasPatrollingAssignment,
  selectActivePatrolSession,
} from 'src/app/state-management/selectors/guard-patrol.selectors';
import { IAumyeState } from 'src/app/state-management/states/aumye.state';
import {
  LocationData,
  LocationService,
} from 'src/app/services/location/location.service';

import { GoogleMap } from '@angular/google-maps';
import { Actions, ofType } from '@ngrx/effects';
import {
  LocationCheckpoint,
  PatrolMonitoringService,
} from 'src/app/services/patrol-monitoring.service';
import { GeofenceResponse } from 'src/app/state-management/states/guard-patrol.state';
import { LocationSosService } from 'src/app/services/geofence/location-sos.service';

interface PatrolAction {
  type: 'start' | 'stop' | 'view';
  label: string;
  icon: string;
  color?: string;
  disabled?: boolean;
}

interface MapMarker {
  position: google.maps.LatLngLiteral;
  label: string;
  icon?: google.maps.Icon | google.maps.Symbol;
}

@Component({
  selector: 'app-patrol-detail-drawer',
  templateUrl: './patrol-detail-drawer.component.html',
  styleUrls: ['./patrol-detail-drawer.component.scss'],
})
export class PatrolDetailDrawerComponent implements OnInit, OnDestroy {
  @ViewChild(GoogleMap) map!: GoogleMap;

  hasAssignment$: Observable<boolean>;
  activeSession: any;
  statistics$: Observable<any>;
  unresolvedViolations$: Observable<any[]>;
  loading$: Observable<boolean>;
  geofences$: Observable<GeofenceResponse[]>;

  startingPatrol = false;
  stoppingPatrol = false;
  currentLocation: LocationData | null = null;
  assignedGeofences: GeofenceResponse[] = [];

  // Map properties
  isGoogleMapsLoaded = false;
  mapCenter: google.maps.LatLngLiteral = { lat: 28.6139, lng: 77.209 }; // Lucknow default
  mapZoom = 15;
  mapOptions: google.maps.MapOptions = {
    mapTypeControl: true,
    streetViewControl: false,
    fullscreenControl: true,
    zoomControl: true,
    mapTypeId: 'roadmap',
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ],
  };
  currentLocationMarker: MapMarker | null = null;

  // Monitoring
  isMonitoring = false;
  monitoringSubscription?: Subscription;
  checkpoints: LocationCheckpoint[] = [];
  violationCount = 0;
  totalChecks = 0;
  inZoneChecks = 0;
  outZoneChecks = 0;
  lastCheckTime: Date | null = null;

  locationAccessOverlay$: BehaviorSubject<{
    isVisible: boolean;
    type: string;
    userName: string;
  }> = new BehaviorSubject<{
    isVisible: boolean;
    type: string;
    userName: string;
  }>({
    isVisible: false,
    type: '',
    userName: '',
  });

  patrolActions: PatrolAction[] = [
    {
      type: 'start',
      label: 'Start Patrol',
      icon: 'play_circle',
      color: '#52c41a',
      disabled: false,
    },
    {
      type: 'stop',
      label: 'Stop Patrol',
      icon: 'stop_circle',
      color: '#ff4d4f',
      disabled: true,
    },
    {
      type: 'view',
      label: 'Your Patrols',
      icon: 'history',
      color: '#1890ff',
      disabled: false,
    },
  ];

  currentLocationDetails$: BehaviorSubject<string> = new BehaviorSubject(
    'Location not available'
  );
  monitoringStatusColor$: BehaviorSubject<string> = new BehaviorSubject(
    '#8c8c8c'
  );
  monitoringStatusText$: BehaviorSubject<string> = new BehaviorSubject(
    'Not Monitoring'
  );
  complianceRate$: BehaviorSubject<number> = new BehaviorSubject(100);
  polygonPaths$: BehaviorSubject<google.maps.LatLngLiteral[]> =
    new BehaviorSubject([]);
  geofenceShapeOptions: any = {};
  geofenceCenterMarker: MapMarker | null = null;
  sessionDuration: string = '0m';
  private sessionDurationInterval?: any;

  private destroy$ = new Subject<void>();
  private loadingSnackBarRef: any;

  constructor(
    @Optional() private dialogRef: MatDialogRef<PatrolDetailDrawerComponent>,
    @Optional()
    private bottomSheetRef: MatBottomSheetRef<PatrolDetailDrawerComponent>,
    private store: Store<IAumyeState>,
    private snackBar: MatSnackBar,
    private locationService: LocationService,
    private locationSosService: LocationSosService,
    private patrolMonitoringService: PatrolMonitoringService,
    private actions$: Actions,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public bottomSheetData: any
  ) {
    // Use either dialog data or bottom sheet data
    this.data = this.dialogData || this.bottomSheetData;
  }

  private data: any;

  ngOnInit(): void {
    this.loading$ = this.store.select(selectPatrolLoading);
    this.checkGoogleMapsAvailability();

    // If data is passed directly, use it; otherwise load from store
    if (this.data) {
      this.initializeWithPassedData();
    } else {
      this.initializeWithStoreData();
    }

    this.assignedGeofences = this.getAssignedGeofences();
    this.getCurrentLocation();

    // Subscribe to active session changes
    this.store.select(selectActivePatrolSession).subscribe((session) => {
      const wasActive = !!this.activeSession;
      this.activeSession = session;

      // Update action button states
      this.updatePatrolActions();

      // If session just started, begin monitoring
      if (session) {
        this.startMonitoring();
        this.startSessionDurationTimer();
      }
      // If session ended, stop monitoring
      else if (!session && wasActive) {
        this.stopMonitoring();
        this.stopSessionDurationTimer();
      }
    });

    this.updateGeofenceData();
    this.updateMonitoringStatus();
  }

  ngOnDestroy(): void {
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
    }
    this.stopSessionDurationTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if Google Maps API is available
   */
  private checkGoogleMapsAvailability(): void {
    if (typeof google !== 'undefined' && google.maps) {
      this.isGoogleMapsLoaded = true;
    } else {
      // Wait for Google Maps to load
      const checkInterval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps) {
          this.isGoogleMapsLoaded = true;
          clearInterval(checkInterval);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!this.isGoogleMapsLoaded) {
          this.showError('Failed to load Google Maps');
        }
      }, 10000);
    }
  }

  private startSessionDurationTimer(): void {
    this.stopSessionDurationTimer();
    this.updateSessionDuration();

    // Update every 30 seconds
    this.sessionDurationInterval = setInterval(() => {
      this.updateSessionDuration();
    }, 30000);
  }

  private updateSessionDuration(): void {
    if (!this.activeSession || !this.activeSession.startedAt) {
      this.sessionDuration = '0m';
      return;
    }

    const start = new Date(this.activeSession.startedAt + 'Z').getTime();
    const now = new Date().getTime();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);

    this.sessionDuration = this.formatTime(diffMins);
  }

  private stopSessionDurationTimer(): void {
    if (this.sessionDurationInterval) {
      clearInterval(this.sessionDurationInterval);
      this.sessionDurationInterval = undefined;
    }
  }

  /**
   * Initialize component with data passed through drawer
   */
  private initializeWithPassedData(): void {
    this.hasAssignment$ = this.store.select(selectHasPatrollingAssignment).pipe(
      tap((hasAssignment) => {
        if (hasAssignment && this.data?.activeSession) {
          this.activeSession = this.data.activeSession;
        }
      })
    );
    this.statistics$ = this.store.select(selectPatrolStatistics);
    this.unresolvedViolations$ = this.store.select(selectPatrolViolations);
    this.geofences$ = this.store.select(selectAssignedGeofences);
  }

  /**
   * Initialize component with store data (when data is not passed)
   */
  private initializeWithStoreData(): void {
    this.store.dispatch(new LoadUserPatrolData());
    this.hasAssignment$ = this.store.select(selectHasPatrollingAssignment);
    this.statistics$ = this.store.select(selectPatrolStatistics);
    this.unresolvedViolations$ = this.store.select(selectPatrolViolations);
    this.geofences$ = this.store.select(selectAssignedGeofences);
  }

  /**
   * Update patrol action button states based on session status
   */
  private updatePatrolActions(): void {
    this.patrolActions = this.patrolActions.map((action) => {
      if (action.type === 'start') {
        return { ...action, disabled: !!this.activeSession };
      } else if (action.type === 'stop') {
        return { ...action, disabled: !this.activeSession };
      }
      return action;
    });
  }

  private updateGeofenceData(): void {
    this.geofences$.subscribe((geofences) => {
      this.assignedGeofences = geofences || [];

      if (this.assignedGeofences.length > 0) {
        const geofence = this.assignedGeofences[0];

        // Update shape options
        this.geofenceShapeOptions = {
          fillColor: geofence.color || '#E93578',
          fillOpacity: 0.2,
          strokeColor: geofence.color || '#E93578',
          strokeOpacity: 0.8,
          strokeWeight: 2,
        };

        // Update polygon paths
        if (geofence.type === 'polygon' && geofence.polygonVertices) {
          const paths = [...geofence.polygonVertices]
            .sort((a, b) => a.order - b.order)
            .map((v) => ({ lat: v.latitude, lng: v.longitude }));
          this.polygonPaths$.next(paths);
        }

        // Update center marker
        if (geofence.type === 'circle') {
          this.geofenceCenterMarker = {
            position: {
              lat: geofence.centerLatitude!,
              lng: geofence.centerLongitude!,
            },
            label: geofence.name,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: geofence.color || '#E93578',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
          };
        }

        setTimeout(() => this.fitMapBounds(), 500);
      }
    });
  }

  onMapReady(): void {
    setTimeout(() => this.fitMapBounds(), 500);
  }

  /**
   * Get current user location using Geolocation API
   */
  private getCurrentLocation(): void {
    this.locationService.getCurrentLocation().subscribe({
      next: (locationData) => {
        this.currentLocation = locationData;
        this.mapCenter = {
          lat: locationData.latitude,
          lng: locationData.longitude,
        };

        // Update current location marker
        this.updateCurrentLocationMarker();

        setTimeout(() => this.fitMapBounds(), 500);

        console.log('Current location:', this.currentLocation);
      },
      error: (error) => {
        console.error('Error getting location:', error);
        this.handleLocationError(error);
      },
    });
  }

  /**
   * Update current location marker on map
   */
  private updateCurrentLocationMarker(): void {
    if (!this.currentLocation) return;

    this.currentLocationMarker = {
      position: {
        lat: this.currentLocation.latitude,
        lng: this.currentLocation.longitude,
      },
      label: 'You',
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#4285F4',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      },
    };

    this.currentLocationDetails$.next(
      `${this.currentLocation.latitude.toFixed(
        6
      )}, ${this.currentLocation.longitude.toFixed(6)}`
    );
  }

  /**
   * Handle location error
   */
  private handleLocationError(error: any): void {
    if (error.code === error.PERMISSION_DENIED) {
      this.showError(
        'Location permission denied. Please enable location access.'
      );
    } else if (error.code === error.POSITION_UNAVAILABLE) {
      this.showError('Location information is unavailable.');
    } else if (error.code === error.TIMEOUT) {
      this.showError('Location request timeout. Please try again.');
    } else {
      this.showError('Unable to get your current location');
    }
  }

  /**
   * Check if a point is inside the geofence (circular or polygon)
   */
  private isPointInsideGeofence(
    point: google.maps.LatLng,
    geofence: GeofenceResponse
  ): boolean {
    if (geofence.type === 'circle') {
      const center = new google.maps.LatLng(
        geofence.centerLatitude!,
        geofence.centerLongitude!
      );
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        point,
        center
      );
      return distance <= geofence.radiusMeters!;
    } else if (geofence.type === 'polygon' && geofence.polygonVertices) {
      const polygonPath = [...geofence.polygonVertices]
        .sort((a, b) => a.order - b.order)
        .map((v) => new google.maps.LatLng(v.latitude, v.longitude));
      const polygon = new google.maps.Polygon({ paths: polygonPath });
      return google.maps.geometry.poly.containsLocation(point, polygon);
    }
    return false;
  }

  /**
   * Fit map bounds to show both geofence and current location
   */
  private fitMapBounds(): void {
    if (!this.isGoogleMapsLoaded || !this.map) return;

    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    // Add current location to bounds
    if (this.currentLocation) {
      bounds.extend(
        new google.maps.LatLng(
          this.currentLocation.latitude,
          this.currentLocation.longitude
        )
      );
      hasPoints = true;
    }

    // Add geofence to bounds
    if (this.assignedGeofences.length > 0) {
      const geofence = this.assignedGeofences[0];

      if (geofence.type === 'circle') {
        // For circle, add center and extend by radius
        const center = new google.maps.LatLng(
          geofence.centerLatitude!,
          geofence.centerLongitude!
        );
        bounds.extend(center);

        // Extend bounds by radius in all directions
        const radiusInDegrees = geofence.radiusMeters! / 111320; // Approximate conversion
        bounds.extend(
          new google.maps.LatLng(
            geofence.centerLatitude! + radiusInDegrees,
            geofence.centerLongitude! + radiusInDegrees
          )
        );
        bounds.extend(
          new google.maps.LatLng(
            geofence.centerLatitude! - radiusInDegrees,
            geofence.centerLongitude! - radiusInDegrees
          )
        );
        hasPoints = true;
      } else if (geofence.type === 'polygon' && geofence.polygonVertices) {
        // For polygon, add all vertices
        geofence.polygonVertices.forEach((vertex) => {
          bounds.extend(
            new google.maps.LatLng(vertex.latitude, vertex.longitude)
          );
        });
        hasPoints = true;
      }
    }

    // Fit bounds with padding
    if (hasPoints) {
      this.map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50,
      });
    }
  }

  /**
   * Get all geofences from store for validation
   */
  private getAssignedGeofences(): GeofenceResponse[] {
    let geofences: GeofenceResponse[] = [];
    this.geofences$.subscribe((data) => {
      geofences = data || [];
      console.log(geofences, 'geofences');
    });
    return geofences;
  }

  /**
   * Check if user is inside the assigned geofence
   */
  private isUserInsideAssignedGeofence(): boolean {
    if (!this.currentLocation) {
      this.showError('Unable to determine your location');
      return false;
    }

    const assignedGeofence = this.assignedGeofences[0];
    if (!assignedGeofence) {
      this.showError('Assigned geofence not found');
      return false;
    }

    const userLocation = new google.maps.LatLng(
      this.currentLocation.latitude,
      this.currentLocation.longitude
    );

    const isInside = this.isPointInsideGeofence(userLocation, assignedGeofence);

    if (!isInside) {
      const distance = this.calculateDistanceToGeofence(
        userLocation,
        assignedGeofence
      );
      this.locationSosService.showLocationAccessOverlay(
        'zone_required',
        'Security Guard'
      );
      this.showWarning(
        `You are outside the geofence. Distance: ${distance.toFixed(2)}m`
      );
    }

    return isInside;
  }

  /**
   * Calculate distance from current location to the nearest point in geofence
   */
  private calculateDistanceToGeofence(
    point: google.maps.LatLng,
    geofence: any
  ): number {
    if (geofence.type === 'circle') {
      const center = new google.maps.LatLng(
        geofence.centerLatitude!,
        geofence.centerLongitude!
      );
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        point,
        center
      );
      return Math.max(0, distance - geofence.radiusMeters!);
    }
    return 0;
  }

  /**
   * Start monitoring patrol location
   */
  private startMonitoring(): void {
    if (!this.activeSession) return;

    const isServiceMonitoring = this.patrolMonitoringService.isActive();
    const currentSessionId = this.patrolMonitoringService.getCurrentSessionId();

    if (isServiceMonitoring && currentSessionId === this.activeSession.id) {
      // Service is already monitoring, just subscribe to updates
      this.isMonitoring = true;
      this.subscribeToMonitoringUpdates();
      console.log('Rejoined existing monitoring session');
      return;
    }

    this.isMonitoring = true;
    this.checkpoints = [];
    this.violationCount = 0;
    this.totalChecks = 0;
    this.inZoneChecks = 0;
    this.outZoneChecks = 0;

    // Start the monitoring service
    this.patrolMonitoringService.startMonitoring(
      this.activeSession.id,
      this.assignedGeofences[0]
    );

    // Subscribe to checkpoint updates
    this.subscribeToMonitoringUpdates();

    this.showSuccess('Location monitoring started');
  }

  private subscribeToMonitoringUpdates(): void {
    // Unsubscribe from any existing subscription
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
    }

    // Subscribe to checkpoint updates
    this.monitoringSubscription = this.patrolMonitoringService
      .getCheckpoints()
      .subscribe((checkpoints) => {
        this.checkpoints = checkpoints;
        this.updateMonitoringStats();
      });
  }

  /**
   * Stop monitoring patrol location
   */
  private stopMonitoring(): void {
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
      this.monitoringSubscription = undefined;
    }
    this.isMonitoring = false;
    this.showInfo('Location monitoring stopped');
    console.log(
      'Drawer monitoring subscription stopped (service still running)'
    );
  }

  /**
   * Update monitoring statistics
   */
  private updateMonitoringStats(): void {
    this.totalChecks = this.checkpoints.length;
    this.inZoneChecks = this.checkpoints.filter((c) => c.isInsideZone).length;
    this.outZoneChecks = this.checkpoints.filter((c) => !c.isInsideZone).length;
    this.violationCount = this.checkpoints.filter(
      (c) => !c.isInsideZone && c.violationReported
    ).length;

    if (this.checkpoints.length > 0) {
      this.lastCheckTime =
        this.checkpoints[this.checkpoints.length - 1].timestamp;
    }

    this.updateMonitoringStatus();
  }

  private updateMonitoringStatus(): void {
    if (!this.isMonitoring) {
      this.monitoringStatusColor$.next('#8c8c8c');
      this.monitoringStatusText$.next('Not Monitoring');
      this.complianceRate$.next(100);
      return;
    }

    const complianceRate =
      this.totalChecks > 0 ? (this.inZoneChecks / this.totalChecks) * 100 : 100;
    this.complianceRate$.next(complianceRate);

    if (complianceRate >= 90) {
      this.monitoringStatusColor$.next('#52c41a');
      this.monitoringStatusText$.next('Excellent');
    } else if (complianceRate >= 70) {
      this.monitoringStatusColor$.next('#faad14');
      this.monitoringStatusText$.next('Good');
    } else {
      this.monitoringStatusColor$.next('#ff4d4f');
      this.monitoringStatusText$.next('Needs Attention');
    }
  }

  /**
   * Get current location details for display
   */
  getCurrentLocationDetails(): string {
    if (!this.currentLocation) {
      return 'Location not available';
    }
    return `${this.currentLocation.latitude.toFixed(
      6
    )}, ${this.currentLocation.longitude.toFixed(6)}`;
  }

  /**
   * Get geofence shape options for map display
   */
  getGeofenceShapeOptions(geofence: GeofenceResponse): any {
    return {
      fillColor: geofence.color || '#E93578',
      fillOpacity: 0.2,
      strokeColor: geofence.color || '#E93578',
      strokeOpacity: 0.8,
      strokeWeight: 2,
    };
  }

  /**
   * Get geofence center marker options
   */
  getGeofenceCenterMarker(geofence: GeofenceResponse): MapMarker | null {
    if (geofence.type === 'circle') {
      return {
        position: {
          lat: geofence.centerLatitude!,
          lng: geofence.centerLongitude!,
        },
        label: geofence.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: geofence.color || '#E93578',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      };
    }
    return null;
  }

  /**
   * Handle Start Patrol action
   */
  onStartPatrol(): void {
    if (this.startingPatrol || this.activeSession) return;

    this.startingPatrol = true;

    // Check if user is inside geofence
    if (!this.isUserInsideAssignedGeofence()) {
      this.startingPatrol = false;
      return;
    }

    // Proceed with starting patrol
    this.loadingSnackBarRef = this.snackBar.open('Starting patrol...', '', {
      duration: 0,
    });

    // Dispatch action to start patrol
    const startPatrolPayload = {
      geofenceId: this.assignedGeofences[0].id,
      startLatitude: this.currentLocation!.latitude,
      startLongitude: this.currentLocation!.longitude,
    };

    this.store.dispatch(new StartPatrolling(startPatrolPayload));

    // Listen for success
    this.actions$
      .pipe(
        ofType('[Guard Patrol] Start Patrolling Success'),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((action: any) => {
        this.loadingSnackBarRef?.dismiss();
        this.showSuccess(`Patrol started in ${this.assignedGeofences[0].name}`);
        this.startingPatrol = false;
      });

    // Listen for failure
    this.actions$
      .pipe(
        ofType('[Guard Patrol] Start Patrolling Failure'),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((action: any) => {
        this.loadingSnackBarRef?.dismiss();
        this.showError('Failed to start patrol. Please try again.');
        this.startingPatrol = false;
      });
  }

  getPolygonPaths(geofence: GeofenceResponse): google.maps.LatLngLiteral[] {
    if (!geofence.polygonVertices || geofence.polygonVertices.length === 0) {
      return [];
    }
    return geofence.polygonVertices
      .sort((a, b) => a.order - b.order)
      .map((v) => ({ lat: v.latitude, lng: v.longitude }));
  }

  /**
   * Handle Stop Patrol action
   */
  async onStopPatrol(): Promise<void> {
    if (this.stoppingPatrol || !this.activeSession) {
      this.showWarning('No active patrol to stop');
      return;
    }

    this.stoppingPatrol = true;
    this.loadingSnackBarRef = this.snackBar.open('Stopping patrol...', '', {
      duration: 0,
    });

    try {
      // Get final monitoring data
      const monitoringData =
        await this.patrolMonitoringService.stopAndGetData();

      // Dispatch action to stop patrol with monitoring data
      this.store.dispatch(
        new EndPatrolling({
          sessionId: this.activeSession.id,
          endLatitude: this.currentLocation?.latitude,
          endLongitude: this.currentLocation?.longitude,
          monitoringData: monitoringData,
        })
      );

      // Listen for success
      this.actions$
        .pipe(
          ofType('[Guard Patrol] End Patrolling Success'),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe((action: any) => {
          this.loadingSnackBarRef?.dismiss();
          this.showSuccess('Patrol stopped successfully');
          this.stoppingPatrol = false;
        });

      // Listen for failure
      this.actions$
        .pipe(
          ofType('[Guard Patrol] End Patrolling Failure'),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe((action: any) => {
          this.loadingSnackBarRef?.dismiss();
          this.showError('Failed to stop patrol. Please try again.');
          this.stoppingPatrol = false;
        });
    } catch (error) {
      console.error('Error stopping patrol:', error);
      this.loadingSnackBarRef?.dismiss();
      this.showError('Failed to stop patrol');
      this.stoppingPatrol = false;
    }
  }

  /**
   * Handle View Patrols action
   */
  onViewPatrols(): void {
    this.showInfo('Opening patrol history...');
    // Navigate to patrols history/list view
    // this.router.navigate(['/patrols/history']);
  }

  /**
   * Handle patrol action button click
   */
  onPatrolAction(action: PatrolAction): void {
    if (action.disabled) return;

    switch (action.type) {
      case 'start':
        this.onStartPatrol();
        break;
      case 'stop':
        this.onStopPatrol();
        break;
      case 'view':
        this.onViewPatrols();
        break;
    }
  }

  /**
   * Refresh patrol data
   */
  onRefreshPatrolData(): void {
    this.store.dispatch(new LoadUserPatrolData());
    this.getCurrentLocation();
  }

  /**
   * Close drawer
   */
  closeDrawer(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  }

  /**
   * Format time in hours and minutes
   */
  formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }

  /**
   * Get monitoring status color
   */
  getMonitoringStatusColor(): string {
    if (!this.isMonitoring) return '#8c8c8c';
    const complianceRate =
      this.totalChecks > 0 ? (this.inZoneChecks / this.totalChecks) * 100 : 100;
    if (complianceRate >= 90) return '#52c41a';
    if (complianceRate >= 70) return '#faad14';
    return '#ff4d4f';
  }

  /**
   * Get monitoring status text
   */
  getMonitoringStatusText(): string {
    if (!this.isMonitoring) return 'Not Monitoring';
    const complianceRate =
      this.totalChecks > 0 ? (this.inZoneChecks / this.totalChecks) * 100 : 100;
    if (complianceRate >= 90) return 'Excellent Compliance';
    if (complianceRate >= 70) return 'Good Compliance';
    return 'Needs Attention';
  }

  /**
   * Center map on current location
   */
  centerOnCurrentLocation(): void {
    if (this.currentLocation) {
      this.mapCenter = {
        lat: this.currentLocation.latitude,
        lng: this.currentLocation.longitude,
      };
      this.mapZoom = 17;
    }
  }

  /**
   * Center map on geofence
   */
  centerOnGeofence(): void {
    if (this.assignedGeofences.length > 0 && this.map) {
      const geofence = this.assignedGeofences[0];
      if (geofence.type === 'circle') {
        this.map.panTo({
          lat: geofence.centerLatitude!,
          lng: geofence.centerLongitude!,
        });
        // Calculate zoom based on radius
        const radiusInKm = geofence.radiusMeters! / 1000;
        this.map.zoom = radiusInKm > 2 ? 13 : radiusInKm > 1 ? 14 : 15;
      } else if (geofence.type === 'polygon') {
        // Use fitBounds for polygon
        this.fitMapBounds();
      }
    }
  }

  fitToView(): void {
    this.fitMapBounds();
  }

  // Snackbar helper methods
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['success-snackbar'],
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['error-snackbar'],
    });
  }

  private showWarning(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['warning-snackbar'],
    });
  }

  private showInfo(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 2000,
      panelClass: ['info-snackbar'],
    });
  }
}
